import { useEffect, useState } from 'react';
import { Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import InputMask from 'react-input-mask';
import PersonIcon from '@mui/icons-material/Person';
import { FormDrawer, FormDrawerGroup, SwitchField } from '../../../../components/form-drawer';
import { FieldError } from '../../../../components/form-page-parts';
import { IParlamentarianAddEditRequest, IParlamentarianContactRequest } from '../../../../model/sge/parlamentarian/request/parlamentarian-add-edit-request';
import { IParlamentarianResponse } from '../../../../model/sge/parlamentarian/response/parlamentarian-response';
import { IPoliticalPartyPagedResponse } from '../../../../model/sge/political-party/response/political-party-paged-response';
import { hasErrors } from '../../../../utils/validation';
import { parseNumberInput } from '../../../../utils/format';
import { PARLAMENTARIAN_TYPES, getParlamentarianTypeLabel, toParlamentarianForm, validateParlamentarian } from './parlamentarian-form-utils';

interface ParlamentarianDrawerProps {
    open: boolean;
    initial?: IParlamentarianResponse;
    politicalParties: IPoliticalPartyPagedResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IParlamentarianAddEditRequest) => void;
}

export function ParlamentarianDrawer({ open, initial, politicalParties, isSaving, onClose, onSave }: ParlamentarianDrawerProps) {
    const [form, setForm] = useState<IParlamentarianAddEditRequest>({ isActive: true });
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const validation = validateParlamentarian(form);
    const errors = showValidationErrors ? validation : {};

    useEffect(() => {
        if (!open) return;
        setForm(toParlamentarianForm(initial));
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IParlamentarianAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function updateContact(patch: Partial<IParlamentarianContactRequest>) {
        setForm((current) => ({ ...current, contact: { ...current.contact, ...patch } }));
    }

    function handleSave() {
        if (hasErrors(validation)) {
            setShowValidationErrors(true);
            return;
        }
        onSave({ ...form, name: form.name!.trim() });
    }

    return (
        <FormDrawer
            open={open}
            icon={<PersonIcon />}
            title={isEdit ? 'Editar parlamentar' : 'Novo parlamentar'}
            subtitle={isEdit ? `${initial?.personName || ''}${initial?.type ? ` · ${getParlamentarianTypeLabel(initial.type)}` : ''}` : undefined}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar parlamentar'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Nome completo</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoFocus
                            autoComplete="off"
                            value={form.name || ''}
                            onChange={(e) => update({ name: e.target.value })}
                            error={!!errors.name}
                        />
                        <FieldError message={errors.name} />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <InputLabel required>Tipo</InputLabel>
                        <Select fullWidth value={form.type ?? ''} onChange={(e) => update({ type: parseNumberInput(e.target.value) })} error={!!errors.type}>
                            <MenuItem value=""><em>Selecionar</em></MenuItem>
                            {PARLAMENTARIAN_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                        </Select>
                        <FieldError message={errors.type} />
                    </Grid>
                    <Grid item xs={12} sm={7}>
                        <InputLabel>Partido político</InputLabel>
                        <Select fullWidth value={form.politicalPartyId ?? ''} onChange={(e) => update({ politicalPartyId: parseNumberInput(e.target.value) })}>
                            <MenuItem value=""><em>Nenhum</em></MenuItem>
                            {politicalParties.map((p) => <MenuItem key={p.id} value={p.id}>{p.abbreviation} — {p.name}</MenuItem>)}
                        </Select>
                    </Grid>
                    <Grid item xs={12}>
                        <SwitchField
                            label="Parlamentar ativo"
                            description="Indica se o parlamentar está em exercício"
                            checked={!!form.isActive}
                            onChange={(isActive) => update({ isActive })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Contato">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel>E-mail</InputLabel>
                        <OutlinedInput fullWidth autoComplete="off" type="email" value={form.contact?.email || ''} onChange={(e) => updateContact({ email: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel>Telefone</InputLabel>
                        <InputMask mask="(99) 9999-9999" value={form.contact?.phone || ''} onChange={(e) => updateContact({ phone: e.target.value })}>
                            <OutlinedInput fullWidth autoComplete="off" />
                        </InputMask>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel>Celular</InputLabel>
                        <InputMask mask="(99) 99999-9999" value={form.contact?.mobile || ''} onChange={(e) => updateContact({ mobile: e.target.value })}>
                            <OutlinedInput fullWidth autoComplete="off" />
                        </InputMask>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel>Tel. comercial</InputLabel>
                        <InputMask mask="(99) 9999-9999" value={form.contact?.commercialPhone || ''} onChange={(e) => updateContact({ commercialPhone: e.target.value })}>
                            <OutlinedInput fullWidth autoComplete="off" />
                        </InputMask>
                    </Grid>
                </Grid>
            </FormDrawerGroup>
        </FormDrawer>
    );
}
